// Effects
import { all, call, put, takeLatest, delay } from 'redux-saga/effects';

import {
  OPTIMIZE_ROUTES,
  OPTIMIZE_ROUTES_SUCCESS,
  OPTIMIZE_ROUTES_FAILURE,
  GET_TASK_STATUS,
  GET_TASK_STATUS_SUCCESS,
  GET_TASK_STATUS_FAILURE,
  GET_ROUTES_BY_DATE,
  GET_ROUTES_BY_DATE_SUCCESS,
  GET_ROUTES_BY_DATE_FAILURE,
  REORDER_ROUTE,
  REORDER_ROUTE_SUCCESS,
  REORDER_ROUTE_FAILURE,
  INSERT_JOB_TO_ROUTE,
  INSERT_JOB_TO_ROUTE_SUCCESS,
  INSERT_JOB_TO_ROUTE_FAILURE,
  UPDATE_JOB_LOCATION,
  UPDATE_JOB_LOCATION_SUCCESS,
  UPDATE_JOB_LOCATION_FAILURE,
  COMPARE_ROUTE_STRATEGIES,
  COMPARE_ROUTE_STRATEGIES_SUCCESS,
  COMPARE_ROUTE_STRATEGIES_FAILURE,
  LOCK_ROUTE,
  LOCK_ROUTE_SUCCESS,
  LOCK_ROUTE_FAILURE,
  DELETE_ROUTE,
  DELETE_ROUTE_SUCCESS,
  DELETE_ROUTE_FAILURE,
  OPTIMIZE_FROM_LOCATION,
  OPTIMIZE_FROM_LOCATION_SUCCESS,
  OPTIMIZE_FROM_LOCATION_FAILURE,
} from '../../modules/constants';

// Service
import { defaultApi } from '../../utils/axiosApi';

// Polling configuration
const POLLING_INTERVAL = 1000; // 1 second
const MAX_POLLING_ATTEMPTS = 300; // 5 minutes

// Helper function to poll task status
function* pollTaskStatus(organizationId, taskId) {
  let attempts = 0;

  while (attempts < MAX_POLLING_ATTEMPTS) {
    try {
      const url = `${organizationId}/route-optimization/api/routes/task-status/${taskId}/`;
      const method = 'GET';
      const response = yield call(defaultApi, url, method);

      const { state, result, error } = response.data;

      // Terminal states
      if (state === 'SUCCESS') {
        return { success: true, result };
      } else if (state === 'FAILURE') {
        return { success: false, error: error || 'Task failed' };
      }

      // Still processing
      yield delay(POLLING_INTERVAL);
      attempts++;
    } catch (err) {
      return { success: false, error: err.message || 'Polling error' };
    }
  }

  return { success: false, error: 'Task polling timeout' };
}

// 1. Optimize Routes (Async)
export function* handleOptimizeRoutes({ payload }) {
  try {
    const { organization_id, date, employee_ids, job_ids, employees_jobs, mode, start_job_id, end_job_id } = payload;

    const url = `${organization_id}/route-optimization/api/routes/optimize/`;
    const method = 'POST';
    
    // Build the data object based on what's provided
    const data = {
      date,
      organization_id,
      ...(mode && { mode }),
    };

    // If employees_jobs is provided (from JobSelectionModal), transform it to flat arrays
    if (employees_jobs && employees_jobs.length > 0) {
      // Extract unique employee IDs
      const extractedEmployeeIds = employees_jobs.map(item => item.employee_id);
      
      // Extract all job IDs (flatten the nested arrays)
      const extractedJobIds = employees_jobs.reduce((acc, item) => {
        return acc.concat(item.job_ids);
      }, []);
      
      // Remove duplicates and add to data
      data.employee_ids = [...new Set(extractedEmployeeIds)];
      data.job_ids = [...new Set(extractedJobIds)];
    } else {
      // Otherwise use employee_ids or job_ids if provided directly
      if (employee_ids && employee_ids.length > 0) {
        data.employee_ids = employee_ids;
      }
      if (job_ids && job_ids.length > 0) {
        data.job_ids = job_ids;
      }
    }

    // Add start and end job IDs if provided
    if (start_job_id) {
      data.start_job_id = start_job_id;
    }
    if (end_job_id) {
      data.end_job_id = end_job_id;
    }

    // Trigger optimization
    const response = yield call(defaultApi, url, method, data);
    const { task_id } = response.data;

    // Poll for completion
    const pollResult = yield call(pollTaskStatus, organization_id, task_id);

    if (pollResult.success) {
      yield put({
        type: OPTIMIZE_ROUTES_SUCCESS,
        payload: { data: pollResult.result, date },
      });
    } else {
      yield put({
        type: OPTIMIZE_ROUTES_FAILURE,
        payload: { error: pollResult.error },
      });
    }
  } catch (err) {
    const errorMessage = err.response?.data?.error || err.response?.data?.detail || err.message || 'Optimization failed';
    yield put({
      type: OPTIMIZE_ROUTES_FAILURE,
      payload: { 
        error: errorMessage,
        response: err.response,
      },
    });
  }
}

// 2. Get Task Status
export function* handleGetTaskStatus({ payload }) {
  try {
    const { organization_id, task_id } = payload;

    const url = `${organization_id}/route-optimization/api/routes/task-status/${task_id}/`;
    const method = 'GET';

    const response = yield call(defaultApi, url, method);

    yield put({
      type: GET_TASK_STATUS_SUCCESS,
      payload: { data: response.data },
    });
  } catch (err) {
    const errorMessage = err.response?.data?.error || err.response?.data?.detail || err.message || 'Failed to get task status';
    yield put({
      type: GET_TASK_STATUS_FAILURE,
      payload: {
        error: errorMessage,
        response: err.response,
      },
    });
  }
}

// 3. Get Routes by Date
export function* handleGetRoutesByDate({ payload }) {
  try {
    const { organization_id, date } = payload;

    const url = `${organization_id}/route-optimization/api/routes/by-date/${date}/`;
    const method = 'GET';

    const response = yield call(defaultApi, url, method);

    yield put({
      type: GET_ROUTES_BY_DATE_SUCCESS,
      payload: { data: response.data, date },
    });
  } catch (err) {
    const errorMessage = err.response?.data?.error || err.response?.data?.detail || err.message || 'Failed to fetch routes';
    yield put({
      type: GET_ROUTES_BY_DATE_FAILURE,
      payload: {
        error: errorMessage,
        response: err.response,
      },
    });
  }
}

// 4. Reorder Route
export function* handleReorderRoute({ payload }) {
  try {
    const { organization_id, route_id, job_sequence_ids } = payload;

    const url = `${organization_id}/route-optimization/api/routes/${route_id}/reorder/`;
    const method = 'PUT';
    const data = { job_sequence_ids };

    const response = yield call(defaultApi, url, method, data);

    yield put({
      type: REORDER_ROUTE_SUCCESS,
      payload: { data: response.data, route_id },
    });
  } catch (err) {
    const errorMessage = err.response?.data?.error || err.response?.data?.detail || err.message || 'Failed to reorder route';
    yield put({
      type: REORDER_ROUTE_FAILURE,
      payload: {
        error: errorMessage,
        response: err.response,
      },
    });
  }
}

// 5. Insert Job to Route
export function* handleInsertJobToRoute({ payload }) {
  try {
    const { organization_id, route_id, job_id, position } = payload;

    const url = `${organization_id}/route-optimization/api/routes/${route_id}/insert-job/`;
    const method = 'POST';
    const data = {
      job_id,
      ...(position !== null && position !== undefined && { position }),
    };

    const response = yield call(defaultApi, url, method, data);

    // Check if it's async response (has task_id)
    if (response.data.task_id) {
      const { task_id } = response.data;

      // Poll for completion
      const pollResult = yield call(pollTaskStatus, organization_id, task_id);

      if (pollResult.success) {
        yield put({
          type: INSERT_JOB_TO_ROUTE_SUCCESS,
          payload: { data: pollResult.result, route_id },
        });
      } else {
        yield put({
          type: INSERT_JOB_TO_ROUTE_FAILURE,
          payload: { error: pollResult.error },
        });
      }
    } else {
      // Sync response
      yield put({
        type: INSERT_JOB_TO_ROUTE_SUCCESS,
        payload: { data: response.data, route_id },
      });
    }
  } catch (err) {
    const errorMessage = err.response?.data?.error || err.response?.data?.detail || err.message || 'Failed to insert job';
    yield put({
      type: INSERT_JOB_TO_ROUTE_FAILURE,
      payload: {
        error: errorMessage,
        response: err.response,
      },
    });
  }
}

// 6. Update Job Location
export function* handleUpdateJobLocation({ payload }) {
  try {
    const { organization_id, job_id, latitude, longitude, correction_method, notes } = payload;

    const url = `${organization_id}/route-optimization/api/jobs/${job_id}/location/`;
    const method = 'PUT';
    const data = {
      latitude,
      longitude,
      ...(correction_method && { correction_method }),
      ...(notes && { notes }),
    };

    const response = yield call(defaultApi, url, method, data);

    yield put({
      type: UPDATE_JOB_LOCATION_SUCCESS,
      payload: { data: response.data, job_id },
    });
  } catch (err) {
    const errorMessage = err.response?.data?.error || err.response?.data?.detail || err.message || 'Failed to update job location';
    yield put({
      type: UPDATE_JOB_LOCATION_FAILURE,
      payload: {
        error: errorMessage,
        response: err.response,
      },
    });
  }
}

// 7. Compare Route Strategies
export function* handleCompareRouteStrategies({ payload }) {
  try {
    const { organization_id, route_id } = payload;

    const url = `${organization_id}/route-optimization/api/routes/${route_id}/compare/`;
    const method = 'GET';

    const response = yield call(defaultApi, url, method);

    yield put({
      type: COMPARE_ROUTE_STRATEGIES_SUCCESS,
      payload: { data: response.data, route_id },
    });
  } catch (err) {
    const errorMessage = err.response?.data?.error || err.response?.data?.detail || err.message || 'Failed to compare strategies';
    yield put({
      type: COMPARE_ROUTE_STRATEGIES_FAILURE,
      payload: {
        error: errorMessage,
        response: err.response,
      },
    });
  }
}

// 8. Lock/Unlock Route
export function* handleLockRoute({ payload }) {
  try {
    const { organization_id, route_id, locked } = payload;

    const url = `${organization_id}/route-optimization/api/routes/${route_id}/lock/`;
    const method = 'POST';
    const data = { locked };

    const response = yield call(defaultApi, url, method, data);

    yield put({
      type: LOCK_ROUTE_SUCCESS,
      payload: { data: response.data, route_id, locked },
    });
  } catch (err) {
    const errorMessage = err.response?.data?.error || err.response?.data?.detail || err.message || 'Failed to lock/unlock route';
    yield put({
      type: LOCK_ROUTE_FAILURE,
      payload: {
        error: errorMessage,
        response: err.response,
      },
    });
  }
}

// 9. Delete Route
export function* handleDeleteRoute({ payload }) {
  try {
    const { organization_id, route_id } = payload;

    const url = `${organization_id}/route-optimization/api/routes/${route_id}/`;
    const method = 'DELETE';

    yield call(defaultApi, url, method);

    yield put({
      type: DELETE_ROUTE_SUCCESS,
      payload: { route_id },
    });
  } catch (err) {
    const errorMessage = err.response?.data?.error || err.response?.data?.detail || err.message || 'Failed to delete route';
    yield put({
      type: DELETE_ROUTE_FAILURE,
      payload: {
        error: errorMessage,
        response: err.response,
      },
    });
  }
}

// 10. Optimize from Custom Location (Async)
export function* handleOptimizeFromLocation({ payload }) {
  try {
    const { organization_id, employee_id, date, start_lat, start_lng, job_ids } = payload;

    const url = `${organization_id}/route-optimization/api/routes/route-from-location/`;
    const method = 'POST';
    const data = {
      employee_id,
      organization_id,
      date,
      start_lat,
      start_lng,
      job_ids,
    };

    // Trigger optimization
    const response = yield call(defaultApi, url, method, data);
    const { task_id } = response.data;

    // Poll for completion
    const pollResult = yield call(pollTaskStatus, organization_id, task_id);

    if (pollResult.success) {
      yield put({
        type: OPTIMIZE_FROM_LOCATION_SUCCESS,
        payload: { data: pollResult.result },
      });
    } else {
      yield put({
        type: OPTIMIZE_FROM_LOCATION_FAILURE,
        payload: { error: pollResult.error },
      });
    }
  } catch (err) {
    const errorMessage = err.response?.data?.error || err.response?.data?.detail || err.message || 'Optimization failed';
    yield put({
      type: OPTIMIZE_FROM_LOCATION_FAILURE,
      payload: {
        error: errorMessage,
        response: err.response,
      },
    });
  }
}

// Root saga
export default function* routeOptimizationSaga() {
  yield all([
    takeLatest(OPTIMIZE_ROUTES, handleOptimizeRoutes),
    takeLatest(GET_TASK_STATUS, handleGetTaskStatus),
    takeLatest(GET_ROUTES_BY_DATE, handleGetRoutesByDate),
    takeLatest(REORDER_ROUTE, handleReorderRoute),
    takeLatest(INSERT_JOB_TO_ROUTE, handleInsertJobToRoute),
    takeLatest(UPDATE_JOB_LOCATION, handleUpdateJobLocation),
    takeLatest(COMPARE_ROUTE_STRATEGIES, handleCompareRouteStrategies),
    takeLatest(LOCK_ROUTE, handleLockRoute),
    takeLatest(DELETE_ROUTE, handleDeleteRoute),
    takeLatest(OPTIMIZE_FROM_LOCATION, handleOptimizeFromLocation),
  ]);
}

